import React, { useEffect, useRef, useState } from 'react';
import { speakLine, stopAllVoices } from './PresentDirector';

/* MenuGuide — after ~30s without interaction in the demo workspace, Rachel's ghost
   cursor tours the left menu: highlights the most valuable links, explains each one
   with her voice, and "clicks" them so the visitor sees the real views.
   Any real human interaction instantly cancels the guide and hands back control. */

const IDLE_MS = 30000;

const GUIDE = [
  { sel: '[data-testid="rest-nav-home"]', click: true,
    es: 'Este menú es tu centro de operaciones. Aquí, en Hoy, te espero cada mañana con tu Daily Brief: lo que ocurrió ayer y lo que conviene hacer hoy — sin hojas de cálculo.',
    en: "This left menu is your day-to-day. Here in Today, I greet you every morning with your Daily Brief: what happened yesterday and what to do today — no spreadsheets." },
  { sel: '[data-testid="rest-nav-intelligence"]', click: true,
    es: 'Intelligence: tu negocio en cifras que se entienden en cinco segundos — visitas, llamadas y reservas desde tu propia web, en tiempo real.',
    en: 'Intelligence: your business in numbers anyone understands in five seconds — visits, calls and reservations from your own website, live.' },
  { sel: '[data-testid="rest-nav-revenue"]', click: true,
    es: 'Y Revenue: cuánto ingresa tu sala, tu propia web y el delivery de terceros — para saber qué canal genera realmente tu facturación.',
    en: 'And Revenue: how much comes from dine-in, your own website and third-party delivery — so you know which channel really makes you money.' },
  { sel: '[data-testid="rest-present-btn"]',
    es: 'Y cuando quieras la experiencia completa, con mi voz y toda la puesta en escena... pulsa aquí. Te espero.',
    en: "And whenever you want the full experience, with my voice and the whole show... click right here. I'll be waiting." },
];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function MenuGuide({ isEs }) {
  const [cursor, setCursor] = useState({ x: -60, y: window.innerHeight / 2, visible: false, clicking: false });
  const [caption, setCaption] = useState(null);
  const [halo, setHalo] = useState(null);
  const [running, setRunning] = useState(false);
  const cancelled = useRef(false);

  // Arm: 30s of true idleness → the guide begins. Any activity resets the clock.
  useEffect(() => {
    if (running) return undefined;
    if (!window.matchMedia('(min-width: 1024px)').matches) return undefined;
    try { if (sessionStorage.getItem('kx_menu_guide_done') === '1') return undefined; } catch { /* noop */ }
    let t;
    const arm = () => { clearTimeout(t); t = setTimeout(() => setRunning(true), IDLE_MS); };
    arm();
    const reset = (e) => { if (e.isTrusted) arm(); };
    const evs = ['pointerdown', 'keydown', 'wheel', 'mousemove', 'touchstart'];
    evs.forEach((ev) => window.addEventListener(ev, reset, true));
    return () => { clearTimeout(t); evs.forEach((ev) => window.removeEventListener(ev, reset, true)); };
  }, [running]);

  useEffect(() => {
    if (!running) return undefined;
    cancelled.current = false;
    try { sessionStorage.setItem('kx_menu_guide_done', '1'); } catch { /* noop */ }
    const lang = isEs ? 'es' : 'en';
    const voice = (() => { try { return localStorage.getItem('kx_rachel_voice') || 'shimmer'; } catch { return 'shimmer'; } })();
    const voiceOn = (() => { try { return localStorage.getItem('kx_present_voice') !== '0'; } catch { return true; } })();

    // The visitor is always in charge — real interaction cancels the guide instantly.
    let moved = 0; let lx = null; let ly = null;
    const stop = () => { if (cancelled.current) return; cancelled.current = true; stopAllVoices(); setRunning(false); };
    const onAct = (e) => { if (e.isTrusted) stop(); };
    const onMove = (e) => {
      if (!e.isTrusted) return;
      if (lx !== null) moved += Math.abs(e.clientX - lx) + Math.abs(e.clientY - ly);
      lx = e.clientX; ly = e.clientY;
      if (moved > 80) stop();
    };
    window.addEventListener('pointerdown', onAct, true);
    window.addEventListener('keydown', onAct, true);
    window.addEventListener('wheel', onAct, true);
    window.addEventListener('touchstart', onAct, true);
    window.addEventListener('mousemove', onMove, true);

    (async () => {
      await sleep(400);
      for (const step of GUIDE) {
        if (cancelled.current) break;
        const el = document.querySelector(step.sel);
        if (!el || el.getBoundingClientRect().width === 0) continue;
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        await sleep(500);
        if (cancelled.current) break;
        const r = el.getBoundingClientRect();
        setHalo({ x: r.left - 5, y: r.top - 5, w: r.width + 10, h: r.height + 10 });
        setCursor({ x: r.left + Math.min(r.width - 16, r.width * 0.55), y: r.top + r.height / 2, visible: true, clicking: false });
        await sleep(1200);
        if (cancelled.current) break;
        const capX = r.right + 350 > window.innerWidth ? Math.max(12, r.left - 336) : r.right + 18;
        const capY = Math.min(Math.max(64, r.top - 6), window.innerHeight - 180);
        setCaption({ text: isEs ? step.es : step.en, x: capX, y: capY });
        let voiceDone = null;
        if (voiceOn) {
          try {
            const played = await speakLine(isEs ? step.es : step.en, lang, voice);
            if (cancelled.current) { played.audio.pause(); break; }
            voiceDone = played.done;
          } catch { voiceDone = null; }
        }
        if (step.click) {
          await sleep(900);
          if (cancelled.current) break;
          setCursor((c) => ({ ...c, clicking: true }));
          try { el.click(); } catch { /* noop */ } // synthetic — isTrusted=false, never cancels the guide
          await sleep(450);
          setCursor((c) => ({ ...c, clicking: false }));
        }
        if (voiceDone) { await Promise.race([voiceDone, sleep(16000)]); await sleep(600); }
        else await sleep(4800);
        if (cancelled.current) break;
        setCaption(null); setHalo(null);
        await sleep(650);
      }
      setCaption(null); setHalo(null);
      setCursor((c) => ({ ...c, visible: false }));
      if (!cancelled.current) { cancelled.current = true; setRunning(false); }
    })();

    return () => {
      cancelled.current = true;
      stopAllVoices();
      window.removeEventListener('pointerdown', onAct, true);
      window.removeEventListener('keydown', onAct, true);
      window.removeEventListener('wheel', onAct, true);
      window.removeEventListener('touchstart', onAct, true);
      window.removeEventListener('mousemove', onMove, true);
      setCaption(null); setHalo(null);
      setCursor({ x: -60, y: window.innerHeight / 2, visible: false, clicking: false });
    };
  }, [running, isEs]);

  if (!running) return null;
  return (
    <>
      {halo && (
        <div className="fixed z-[84] pointer-events-none rounded-xl" data-testid="menu-guide-halo"
          style={{ left: halo.x, top: halo.y, width: halo.w, height: halo.h,
            border: '2px solid #F77F11', boxShadow: '0 0 0 4px rgba(247,127,17,0.18), 0 0 24px rgba(247,127,17,0.35)',
            animation: 'kx-guide-halo 1.6s ease-in-out infinite', transition: 'left 0.6s ease, top 0.6s ease' }} />
      )}
      <div className="fixed z-[88] pointer-events-none" data-testid="menu-guide-cursor"
        style={{ left: 0, top: 0, opacity: cursor.visible ? 1 : 0,
          transform: `translate(${cursor.x}px, ${cursor.y}px)`,
          transition: 'transform 1.15s cubic-bezier(0.3,0.9,0.35,1), opacity 0.6s ease' }}>
        {cursor.clicking && (
          <span className="absolute -left-5 -top-5 w-10 h-10 rounded-full border-2 border-[#F77F11]"
            style={{ animation: 'kx-guide-ripple 0.55s ease-out both' }} />
        )}
        <svg width="26" height="26" viewBox="0 0 24 24" style={{ transform: cursor.clicking ? 'scale(0.82)' : 'scale(1)', transition: 'transform 0.18s ease', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.45))' }}>
          <path d="M5.5 3.2 19 11.6l-6.2 1.2-3.1 5.7z" fill="#fff" stroke="#1a1b1e" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
      </div>
      {caption && (
        <div className="fixed z-[87] pointer-events-none" data-testid="menu-guide-caption"
          style={{ left: caption.x, top: caption.y, maxWidth: 330, animation: 'kx-guide-caption 0.7s cubic-bezier(0.16,1,0.3,1) both' }}>
          <div className="rounded-2xl px-4 py-3 shadow-2xl border border-white/15"
            style={{ background: 'rgba(18,19,22,0.93)', backdropFilter: 'blur(16px)' }}>
            <p className="text-[9.5px] uppercase tracking-[0.25em] font-bold text-[#F77F11] mb-1">
              Rachel · {isEs ? 'te muestro el menú' : 'menu walkthrough'}
            </p>
            <p className="text-[13px] leading-relaxed text-white/95" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              {caption.text}
            </p>
            <p className="mt-1.5 text-[10px] text-white/40">{isEs ? 'Toca o mueve el ratón para tomar el control' : 'Click or move your mouse to take over'}</p>
          </div>
        </div>
      )}
      <style>{`
        @keyframes kx-guide-halo { 0%, 100% { box-shadow: 0 0 0 4px rgba(247,127,17,0.18), 0 0 24px rgba(247,127,17,0.35); } 50% { box-shadow: 0 0 0 7px rgba(247,127,17,0.08), 0 0 34px rgba(247,127,17,0.5); } }
        @keyframes kx-guide-ripple { 0% { transform: scale(0.3); opacity: 0.9; } 100% { transform: scale(1.6); opacity: 0; } }
        @keyframes kx-guide-caption { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }
      `}</style>
    </>
  );
}
